import {
  ImageScaleView,
  ImageView,
  View,
  animate,
  type ViewOpts,
} from '@play-co/timestep-core/ui';
import { SpineView } from '@play-co/timestep-spine';
import sounds from 'src/lib/sounds';
import type { DreamDriftHero } from 'src/replicant/dreamDrift/dreamDrift.ruleset';
import type { DreamDriftMapConfig } from '../dreamDrift.theme';
import DreamDriftAvatarPin from './DreamDriftAvatarPin';
import DreamDriftMapScrollView from './DreamDriftMapScrollView';
import DreamDriftNode, { type DreamDriftNodeStatus } from './DreamDriftNode';

/** Vertical offset between a progression node and the avatar pin tip. */
const AVATAR_PIN_OFFSET_Y = 40;

/** Duration of one avatar hop between adjacent progression nodes. */
const AVATAR_PIN_JUMP_DURATION = 500;

/** Source-coordinate height added to the midpoint of one avatar hop. */
const AVATAR_PIN_JUMP_HEIGHT = 120;

/** Duration of the final bounce before a completed pin disappears. */
const AVATAR_PIN_COMPLETION_DURATION = 500;

/** Scaled map position used by nodes, scrolling, and avatar movement. */
type DreamDriftMapNodePosition = {
  x: number;
  y: number;
};

/** Values required to configure the Dream Drift map viewport and content. */
type DreamDriftMapSetupOpts = {
  /** Player avatar displayed at the current progression node. */
  avatar: string;
  /** Loaded source map dimensions, slices, chests, animations, and node positions. */
  config: DreamDriftMapConfig;
  finalNode: number;
  checkpoints: number[];
  /** Resolve one configured image filename through the active theme. */
  getImagePath: (image: string) => string;
  /** Height of the visible scroll viewport. */
  height: number;
  /** Hero selected for the current Dream Drift progression segment. */
  hero?: DreamDriftHero;
  /** Highest Dream Drift progression node already cleared. */
  progress: number;
};

/** Reusable vertically scrollable Dream Drift map view. */
export default class DreamDriftMap extends View {
  /** Scroll viewport containing the complete progression map. */
  private scroll?: DreamDriftMapScrollView;

  /** Reusable progression nodes in their configured path order. */
  private nodes: DreamDriftNode[] = [];

  private checkpoints: number[] = [];

  /** Scaled position of every progression node in path order. */
  private nodePositions: DreamDriftMapNodePosition[] = [];

  /** Reusable player pin moved between progression nodes. */
  private avatarPin?: DreamDriftAvatarPin;

  /** Scale applied to source-coordinate map and pin artwork. */
  private mapScale = 1;

  /**
   * Create an empty map view that clips its configured scroll viewport.
   *
   * @param opts Base view options including the parent and map width.
   */
  constructor(opts: ViewOpts) {
    // Preserve the requested layout while clipping all map content to this view.
    super({
      ...opts,
      clip: true,
    });
  }

  /**
   * Rebuild the viewport with background slices, chests, looping characters, and nodes.
   *
   * @param opts Map configuration, progress, theme resolver, and viewport height.
   */
  public setup({
    avatar,
    config,
    finalNode,
    checkpoints,
    getImagePath,
    height,
    hero,
    progress,
  }: DreamDriftMapSetupOpts): void {
    // Remove the previous scroll hierarchy when reconfiguring this component.
    this.removeAllSubviews();
    this.nodes = [];
    this.checkpoints = checkpoints;
    this.nodePositions = [];
    this.avatarPin = undefined;

    const nodes = finalNode === 12 ? config.nodes12 : config.nodes;
    const chests = finalNode === 12 ? config.chests12 : config.chests;

    // Resize the map component to the requested viewport height.
    this.updateOpts({ height });
    const width = this.style.width;

    // Resolve the complete map dimensions before constructing its scroll bounds.
    this.mapScale = width / config.dimensions.width;
    const mapHeight = config.dimensions.height * this.mapScale;
    const sliceHeight = mapHeight / config.numSlices;

    // Use explicit bounds so initial positioning works before the first tick.
    this.scroll = new DreamDriftMapScrollView({
      superview: this,
      width,
      height,
      scrollX: false,
      scrollY: true,
      scrollBounds: {
        minX: 0,
        minY: 0,
        maxX: width,
        maxY: mapHeight,
      },
    });

    // Keep every map slice inside one scrollable content container.
    const mapContainer = new View({
      superview: this.scroll,
      width,
      height: mapHeight,
    });

    // Place the source images sequentially to recreate the complete map strip.
    config.order.forEach((image, index) => {
      new ImageScaleView({
        superview: mapContainer,
        image: getImagePath(image),
        width,
        height: sliceHeight,
        x: 0,
        y: sliceHeight * index,
      });
    });

    // Place decorative milestone chests at their source size and configured layer.
    chests.forEach(({ image, x, y, zIndex }) => {
      new ImageView({
        superview: mapContainer,
        image: getImagePath(image),
        autoSize: true,
        x: x * this.mapScale,
        y: y * this.mapScale,
        zIndex,
        scale: this.mapScale,
        canHandleEvents: false,
      });
    });

    // Separate skeletons keep each animation's attachment keys from hiding its peers.
    const spineConfig = config.spine;
    spineConfig?.animations.forEach(({ id, x, y, scale, flipX }) => {
      // Place the character above the slices and below every progression node.
      const spine = new SpineView({
        superview: mapContainer,
        spinePath: spineConfig.path,
        x: x * this.mapScale,
        y: y * this.mapScale,
        scale: scale * this.mapScale,
        flipX,
        canHandleEvents: false,
      });

      // Apply the character's initial pose immediately on every map setup.
      spine.state.setAnimation(0, id, true);
      spine.state.apply(spine.skeleton);
    });

    // Build every progression node from its source-coordinate map description.
    nodes.forEach(({ x, y }, index) => {
      // Scale the shared source position once for both the node and avatar pin.
      const nodeX = x * this.mapScale;
      const nodeY = y * this.mapScale;
      this.nodePositions.push({ x: nodeX, y: nodeY });

      // Earlier path nodes render above every later overlapping node.
      const zIndex = nodes.length - index;

      // Scale both node position and artwork with the reconstructed map.
      const node = new DreamDriftNode({
        superview: mapContainer,
        x: nodeX,
        y: nodeY,
        scale: this.mapScale,
        zIndex,
      });

      // Retain the node so its status can change throughout the animation.
      this.nodes.push(node);
    });

    // Create one reusable avatar pin above the complete node layer.
    this.avatarPin = new DreamDriftAvatarPin({
      superview: mapContainer,
      scale: this.mapScale,
      zIndex: nodes.length + 1,
    });

    // Display the current player's avatar and selected hero inside the reusable pin.
    this.avatarPin.setup({ avatar, hero });

    // Render the source while leaving the viewport at its opening position.
    this.applyProgress(progress);
  }

  /**
   * Animate the avatar and node states with hop sounds, celebrating forward progress only.
   *
   * @param fromProgress Previously displayed progress position.
   * @param toProgress Current authoritative progress position.
   * @returns Promise resolved after the complete map presentation finishes.
   */
  public async animateProgress(
    fromProgress: number,
    toProgress: number,
  ): Promise<void> {
    // Empty map configurations have no visual progress to present.
    const finalProgress = this.nodePositions.length;
    if (finalProgress === 0) return;

    // Invalid persisted values fall back to a safe static destination.
    const sourceIsValid =
      Number.isInteger(fromProgress) &&
      fromProgress >= 0 &&
      fromProgress <= finalProgress;
    const destinationIsValid =
      Number.isInteger(toProgress) &&
      toProgress >= 0 &&
      toProgress <= finalProgress;
    if (!sourceIsValid || !destinationIsValid) {
      const safeProgress = destinationIsValid ? toProgress : 0;
      this.applyProgress(safeProgress);
      await this.scrollToProgress(safeProgress, 0);
      return;
    }

    // Restore the receipt's source before moving its avatar pin.
    this.applyProgress(fromProgress);

    // Static presentations still need their exact final node state applied.
    if (fromProgress === toProgress) {
      this.applyProgress(toProgress);
      return;
    }

    // A completed source has no pin position from which movement can resume.
    if (fromProgress === finalProgress) {
      this.applyProgress(toProgress);
      await this.scrollToProgress(toProgress, 0);
      return;
    }

    // Regression tells one clear story by jumping directly to the checkpoint.
    if (toProgress < fromProgress) {
      sounds.sfx.play('button-power');
      await this.jumpToProgress(toProgress);
      this.applyProgress(toProgress);
      return;
    }

    // Celebrate forward progress once before the pin starts moving.
    sounds.sfx.play('win');

    // Progression moves through each newly travelled node in path order.
    const positionedDestination = Math.min(toProgress, finalProgress - 1);
    let currentProgress = fromProgress;
    while (currentProgress !== positionedDestination) {
      currentProgress += 1;
      sounds.sfx.play('button-power');
      await this.jumpToProgress(currentProgress);
      this.applyProgress(currentProgress);
    }

    // Completion clears the final node and removes the now-terminal pin.
    if (toProgress === finalProgress) {
      sounds.sfx.play('button-power');
      await this.animateCompletion();
      this.applyProgress(toProgress);
      return;
    }

    // Non-terminal transitions finish at the authoritative current node.
    this.applyProgress(toProgress);
  }

  /**
   * Update the hero displayed by the current avatar pin.
   *
   * @param hero Newly confirmed Dream Drift hero, when available.
   * @returns Nothing.
   */
  public setHero(hero?: DreamDriftHero): void {
    // Keep the existing avatar while refreshing its hero badge and border.
    this.avatarPin?.setHero(hero);
  }

  /**
   * Position the viewport at the source pin before its progress story begins.
   *
   * @param progress Source progress position to bring into view.
   * @returns Promise resolved after the viewport reaches the source pin.
   */
  public async focusProgress(progress: number): Promise<void> {
    // Empty maps have no pin position to focus.
    if (this.nodePositions.length === 0) return;

    // Keep invalid persisted values within the configured map path.
    const finalProgress = this.nodePositions.length;
    const progressIsValid =
      Number.isInteger(progress) && progress >= 0 && progress <= finalProgress;
    const safeProgress = progressIsValid ? progress : 0;

    // Position the source pin immediately without an opening scroll animation.
    await this.scrollToProgress(safeProgress, 0);
  }

  /**
   * Apply node statuses and pin placement for one logical progress value.
   *
   * @param progress Highest cleared progress represented by the map.
   * @returns Nothing.
   */
  private applyProgress(progress: number): void {
    // Update every node from the same logical progress snapshot.
    this.nodes.forEach((node, index) => {
      // Later nodes remain open until they become the next playable node.
      let status: DreamDriftNodeStatus = 'open';

      // Node indexes below progress have already been cleared.
      if (index < progress) status = 'cleared';

      // Progress points at the current zero-based playable node index.
      else if (index === progress) status = 'current';

      // Milestones use one-based level numbers while map nodes use zero-based indexes.
      const isCheckpoint = this.checkpoints.includes(index + 1);

      // Apply the derived status without rebuilding the node hierarchy.
      node.setup({ index, isCheckpoint, status });
    });

    // Completed progress has no next playable node or visible avatar pin.
    const position = this.nodePositions[progress];
    if (!position || !this.avatarPin) {
      this.avatarPin?.hide();
      return;
    }

    // Restore pin visibility and place its tip above the current node.
    this.avatarPin.show();
    this.avatarPin.updateOpts({
      x: position.x,
      y: position.y - AVATAR_PIN_OFFSET_Y,
      opacity: 1,
      scale: this.mapScale,
    });
  }

  /**
   * Jump the avatar pin to one adjacent progression position.
   *
   * @param progress Destination progress index for this hop.
   * @returns Promise resolved after pin and scroll movement completes.
   */
  private async jumpToProgress(progress: number): Promise<void> {
    // Missing runtime views cannot animate and should fail safely.
    const position = this.nodePositions[progress];
    const avatarPin = this.avatarPin;
    if (!position || !avatarPin) return;

    // Build a raised midpoint so the movement reads as a jump, not a slide.
    const destinationY = position.y - AVATAR_PIN_OFFSET_Y;
    const midpointY =
      Math.min(avatarPin.style.y, destinationY) -
      AVATAR_PIN_JUMP_HEIGHT * this.mapScale;
    const halfDuration = AVATAR_PIN_JUMP_DURATION / 2;

    // Follow the pin with the map viewport throughout the complete hop.
    const scrollPromise = this.scrollToProgress(
      progress,
      AVATAR_PIN_JUMP_DURATION,
    );

    // Move horizontally at a constant speed throughout the complete jump.
    const horizontalPromise = animate(avatarPin.style)
      .now({ x: position.x }, AVATAR_PIN_JUMP_DURATION, animate.linear)
      .toPromise();

    // Raise and lower independently so the vertical motion forms a jump arc.
    const verticalPromise = animate(avatarPin)
      .now({ y: midpointY }, halfDuration, animate.easeOut)
      .then({ y: destinationY }, halfDuration, animate.easeIn)
      .toPromise();

    // Finish every concurrent movement before starting another hop.
    await Promise.all([horizontalPromise, verticalPromise, scrollPromise]);
  }

  /**
   * Bounce and fade the pin after the final progression node is cleared.
   *
   * @returns Promise resolved after the completion presentation finishes.
   */
  private async animateCompletion(): Promise<void> {
    // Completed maps without an active pin need no terminal animation.
    const avatarPin = this.avatarPin;
    if (!avatarPin) return;

    // Lift and fade the pin before the completed map hides it permanently.
    await animate(avatarPin)
      .now(
        {
          y: avatarPin.style.y - AVATAR_PIN_JUMP_HEIGHT * this.mapScale,
          scale: this.mapScale * 1.1,
        },
        AVATAR_PIN_COMPLETION_DURATION / 2,
        animate.easeOut,
      )
      .then(
        { opacity: 0, scale: this.mapScale },
        AVATAR_PIN_COMPLETION_DURATION / 2,
        animate.easeIn,
      )
      .toPromise();
  }

  /**
   * Center the map viewport on one logical progress position.
   *
   * @param progress Progress position to bring into view.
   * @param duration Scroll animation duration in milliseconds.
   * @returns Promise resolved after the viewport reaches its destination.
   */
  private async scrollToProgress(
    progress: number,
    duration: number,
  ): Promise<void> {
    // Missing scroll state cannot reposition the viewport.
    const scroll = this.scroll;
    if (!scroll) return;

    // Completed progress centers on the final configured node.
    const positionIndex = Math.min(
      Math.max(0, progress),
      this.nodePositions.length - 1,
    );
    const position = this.nodePositions[positionIndex];
    if (!position) return;

    // Center the requested node before ScrollView clamps against map bounds.
    const scrollY = Math.max(0, position.y - this.style.height / 2);

    // Instant positioning must synchronize ScrollView's internal virtual offset.
    if (duration === 0) {
      scroll.scrollToImmediate(0, scrollY);
      return;
    }

    // Follow-up scrolls resolve only after ScrollView invokes its callback.
    await new Promise<void>((resolve) => {
      scroll.scrollTo(0, scrollY, duration, resolve);
    });
  }
}
